import { gql } from '@apollo/client';
import {
    CardFragment,
    SearchDocument,
    SearchFragment,
    SearchPageQuery,
    SearchQuery,
    SearchQueryVariables,
    useSearchPageAllAvailableFiltersQuery,
    useSearchPageQuery,
} from '../../generated/graphql';
import { defaultLanguage } from '../../pages/_app';
import { useRouter } from 'next/router';
import { ReactNode, useMemo } from 'react';
import Page from '@oberoninternal/travelbase-website/dist/components/Page';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Flex } from '@rebass/grid';
import Formed from '@oberoninternal/travelbase-website/dist/components/Formed';
import TagButton from '@oberoninternal/travelbase-website/dist/components/TagButton';
import { CheckboxField } from '@oberoninternal/travelbase-ds/components/form/Checkbox';
import Card from '../Card';
import BottomSearch from '../BottomSearch';
import Stack from '@oberoninternal/travelbase-website/dist/components/Stack';
import { useThemeContext } from '@oberoninternal/travelbase-ds/hooks/useThemeContext';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { ParsedUrlQuery } from 'querystring';
import ContentWrapper from '@oberoninternal/travelbase-website/dist/components/ContentWrapper';
import { searchHitToCard } from '../../utils/fragmentConverters';
import Seo from '../Seo';
import addGlobalQuery, { GlobalProps } from '../../utils/addGlobalQuery';
import { GetStaticProps } from 'next';
import initApolloClient, { PreviewData } from '../../initApolloClient';
import { addApolloState } from '@oberoninternal/travelbase-website/dist/createApolloClient';
import { DEFAULT_REVALIDATION_TIME } from '../../constants/revalidationTime';
import Spinner from '@oberoninternal/travelbase-ds/components/figure/Spinner';

export const query = gql`
    query SearchPage($lang: String!, $q: String!, $types: [String!]) {
        globalSearch(language: $lang, query: $q, limit: 50, type: $types) {
            totalHits
            typeCounts {
                count
                label
            }
            hits {
                ...SearchHit
            }
        }
    }

    query SearchPageAllAvailableFilters($lang: String!, $q: String!) {
        globalSearch(language: $lang, query: $q, limit: 50) {
            totalHits
            typeCounts {
                count
                label
            }
        }
    }

    query Search($lang: String) {
        entry(section: ["search"], site: [$lang]) {
            ...Search
        }
    }

    fragment Search on search_Entry {
        title
        settingsCookieConsent
        settingsMetaDescription
        settingsSeoTitle

        localized {
            url
            language
        }
    }

    fragment SearchHit on SearchType {
        postDate
        title
        type
        uri
        image {
            extension
            placeholderUrl
            srcset
            srcsetWebp
            title
            url
        }
    }
`;

export const filterTypes: Array<{ label: ReactNode; type: string }> = [
    {
        label: <FormattedMessage defaultMessage="Informatie" />,
        type: 'pages',
    },
    {
        label: <FormattedMessage defaultMessage="Nieuws" />,
        type: 'articles',
    },
    {
        label: <FormattedMessage defaultMessage="Accommodaties" />,
        type: 'accommodation',
    },
    {
        label: <FormattedMessage defaultMessage="Bedrijven" />,
        type: 'company',
    },
    {
        label: <FormattedMessage defaultMessage="Excursies" />,
        type: 'activity',
    },
];

const SearchLoading = (): React.JSX.Element => (
    <Flex flexDirection="column">
        <Body>
            <strong>
                <FormattedMessage defaultMessage="Bezig met zoeken" />
            </strong>
        </Body>
        <Body variant="small">
            <FormattedMessage defaultMessage="Even geduld alsjeblieft..." />
        </Body>
        <Spinner style={{ marginTop: '1rem' }} />
    </Flex>
);

const SearchNoResults = (): React.JSX.Element => (
    <Flex flexDirection="column">
        <Body>
            <strong>
                <FormattedMessage defaultMessage="Geen resultaten gevonden" />
            </strong>
        </Body>
        <Body variant="small">
            <FormattedMessage defaultMessage="Probeer uw zoekopdracht aan te passen." />
        </Body>
    </Flex>
);

const SearchResults = ({ cards }: { cards: CardFragment[] }): React.JSX.Element => (
    <Grid>
        {cards.map(card => (
            <Card key={card.uri} item={card} dontPrefixLocale />
        ))}
    </Grid>
);

const Search = ({ entry }: { entry: SearchFragment } & GlobalProps) => {
    const {
        query: { q, types: typesString },
        locale,
        push,
        pathname,
    } = useRouter();
    const types = (typesString as string)?.split(',') ?? [];

    const { data, loading } = useSearchPageQuery({
        ssr: false,
        variables: {
            lang: (locale as string) ?? defaultLanguage,
            q: (q as string) ?? '',
            types: types.length > 0 ? types : undefined,
        },
    });

    // This is needed to display all available types after selecting a filter
    // When filtering the result only returns the selected type, thus not allowing to see which
    // types are available without a selecting filter
    const { data: allTypesData } = useSearchPageAllAvailableFiltersQuery({
        ssr: false,
        variables: {
            lang: (locale as string) ?? defaultLanguage,
            q: (q as string) ?? '',
        },
    });

    const { formatDate } = useIntl();
    const cards = useMemo(() => searchHitToCard(data?.globalSearch, formatDate), [data, formatDate]);
    const theme = useThemeContext();

    let MainContent: () => React.JSX.Element;
    if (loading) {
        MainContent = SearchLoading;
    } else if (cards.length > 0) {
        MainContent = () => <SearchResults cards={cards} />;
    } else {
        MainContent = SearchNoResults;
    }

    const availableFilters = getAvailableFilters(data);
    const availableFiltersAfterFiltering = getAvailableFilters(allTypesData);

    return (
        <Page>
            <Seo
                settingsMetaDescription={entry.settingsMetaDescription ?? ''}
                settingsSeoTitle={entry.settingsSeoTitle ?? entry.title ?? ''}
                alternates={entry.localized}
            />

            <Box py={[8]}>
                <ContentWrapper>
                    <Flex flexDirection={['column', 'column', 'row']}>
                        <SearchBar>
                            <Formed
                                skipPrompt
                                initialValues={Object.assign(
                                    {},
                                    ...availableFilters.map(({ type }) => ({ [type]: types.includes(type) }))
                                )}
                                handleAutoSave={async v => {
                                    const selectedTypes = Object.entries(v)
                                        .filter(([, val]) => val)
                                        .map(([key]) => key);

                                    const urlQuery: ParsedUrlQuery = { q: q as string };
                                    if (selectedTypes.length > 0) {
                                        urlQuery.types = selectedTypes.join(',');
                                    }

                                    await push({
                                        pathname,
                                        query: urlQuery,
                                    });
                                }}
                            >
                                {({ setFieldValue, values, setValues }) => {
                                    const selectedFilters = availableFilters.filter(({ type }) => values[type]);
                                    return (
                                        <>
                                            <Stack spacing={1}>
                                                <Body style={{ fontWeight: 600 }}>
                                                    <FormattedMessage
                                                        defaultMessage="Resultaten voor <colored>’{query}’</colored>"
                                                        values={{
                                                            query: q as string,
                                                            colored: (children: ReactNode) => (
                                                                <span style={{ color: theme.colors.primary[40] }}>
                                                                    {children}
                                                                </span>
                                                            ),
                                                        }}
                                                    />
                                                </Body>
                                                <Body style={{ color: theme.colors.neutral[50] }} variant="small">
                                                    <FormattedMessage
                                                        defaultMessage="<strong>{amount}</strong> resultaten"
                                                        values={{
                                                            amount: data?.globalSearch?.hits?.length,
                                                            strong: (children: ReactNode) => (
                                                                <strong style={{ fontWeight: 700 }}>{children}</strong>
                                                            ),
                                                        }}
                                                    />
                                                </Body>
                                            </Stack>
                                            {selectedFilters.length > 0 && (
                                                <>
                                                    <Box mt={7} mb={1} className="gt-s">
                                                        <Header>
                                                            <FormattedMessage defaultMessage="Geselecteerde filters" />
                                                        </Header>
                                                    </Box>
                                                    <Box>
                                                        {selectedFilters.map(filter => (
                                                            <Box
                                                                mr={[4, 3]}
                                                                mt={[4, 3]}
                                                                key={filter.type}
                                                                style={{ display: 'inline-block' }}
                                                            >
                                                                <TagButton
                                                                    type="button"
                                                                    onClick={() => setFieldValue(filter.type, false)}
                                                                >
                                                                    {filter.label}
                                                                </TagButton>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </>
                                            )}

                                            <Box py={5} className="lt-s">
                                                <Divider />
                                            </Box>

                                            <Box className="gt-s">
                                                {availableFiltersAfterFiltering.length > 0 && (
                                                    <Box mt={6} mb={4}>
                                                        <Header>
                                                            <FormattedMessage defaultMessage="Verfijn resultaten" />
                                                        </Header>
                                                    </Box>
                                                )}
                                                <Stack spacing={3}>
                                                    {availableFiltersAfterFiltering.map(filter => (
                                                        <CheckboxField
                                                            key={filter.type}
                                                            name={filter.type}
                                                            id={filter.type}
                                                        >
                                                            {filter.label}
                                                        </CheckboxField>
                                                    ))}
                                                </Stack>
                                            </Box>

                                            <BottomSearch values={values} onSetValues={v => setValues(v)} />
                                        </>
                                    );
                                }}
                            </Formed>
                        </SearchBar>

                        <MainContent />
                    </Flex>
                </ContentWrapper>
            </Box>
        </Page>
    );
};
export default Search;

export const getStaticProps: GetStaticProps = async context => {
    if (context.locale === 'default') return { props: {} };

    const { previewData } = context;
    const client = initApolloClient(context.locale as string, { previewData: previewData as PreviewData });
    const lang = context.locale as string;

    const { data } = await client.query<SearchQuery, SearchQueryVariables>({
        query: SearchDocument,
        variables: { lang },
    });

    const suppressCookieConsent = !((data.entry as { settingsCookieConsent?: boolean }).settingsCookieConsent ?? false);

    return {
        ...addApolloState(
            client,
            (await addGlobalQuery(
                {
                    props: {
                        entry: data.entry,
                        suppressCookieConsent,
                    },
                },
                context.locale
            )) as { props: Record<string, unknown> }
        ),
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

// Get available filters based on query result
const getAvailableFilters = (searchPageData: SearchPageQuery | undefined) => {
    if (!searchPageData || !searchPageData.globalSearch?.typeCounts) {
        return filterTypes;
    }

    const { typeCounts } = searchPageData.globalSearch;
    const availableLabels = typeCounts.map(typeCount => typeCount?.label);
    const availableFilters = filterTypes.filter(filterType => availableLabels.includes(filterType.type));

    return availableFilters;
};

const Divider = styled.div`
    height: 1px;
    border: 1px solid ${({ theme }) => theme.colors.neutral[20]};
`;

const Grid = styled.div`
    flex: 1;
    display: grid;
    row-gap: 3.2rem;
    column-gap: 3.2rem;
    position: relative;
    grid-template-columns: 1fr;

    > * {
        margin-bottom: 0;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        grid-template-columns: 1fr 1fr;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        row-gap: 6rem;
        grid-template-columns: 1fr 1fr 1fr;
    }
`;

const SearchBar = styled.div`
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 27.6rem;
        margin-right: 7.6rem;
        padding-right: ${({ theme }) => theme.spacing['50_Semi']};
        border-right: 1px solid ${({ theme }) => theme.colors.neutral[30]};
    }
`;

const Header = styled.h6`
    font-weight: 700;
    font-size: 1.3rem;
    letter-spacing: 0;
    line-height: 20px;
    margin: 0;
`;
